import React, { useRef, forwardRef } from 'react';
import { ClassValue } from 'classnames/types';
import cn from 'classnames';
import Div from '../Element/Div/Div';
import BodyText from '../Typography/BodyText/BodyText';
import Gutter from '../Gutter/Gutter';
import Flex from '../Flex/Flex';
import Span from '../Element/Span/Span';
import { SupportedColor, CommonProps } from '../Element/Element';
import { SupportedIconName } from '../../shared/icons';
import styles from './BaseInput.module.scss';
import { ArrayElementType } from '../../types/utils/ArrayElementType';
import ResolveIcon from './ResolveIcon';
import 'focus-within-polyfill';
import getBorderRadiusClasses, {
  BorderRadiusProps,
} from '../Element/getBorderRadiusClasses';
import InputMessage from './InputMessage/InputMessage';

type BaseInputCommonProps = {
  /** Label shown on the input */
  label?: string;
  /** Adds the required mark on the input */
  required?: boolean;
  /** Sets the input to disabled state */
  disabled?: boolean;
  /** Shows the confirmed sign in the input */
  confirmed?: boolean;
  /** Change Color of icon to purple500 if input is valid aka filled */
  valid?: boolean;
  /** Error text shown below the input to indicate the errorneous state */
  errorText?: string;
  /** Additional description for input, if label is not enough */
  helperText?: string;
  /** Custom className */
  className?: ClassValue;
  /** Hide the existing label in order to show customized label (for PhoneNumberInput) */
  showCustomLabel?: boolean;
  /** WrapperTabIndex effects on the focusability of the outer wrapper div, if you want to effect on the inner input element use inputTabIndex */
  wrapperTabIndex?: number;
} & Pick<CommonProps<'div'>, 'borderRadius'> &
  Pick<
    JSX.IntrinsicElements['div'],
    'onClick' | 'onBlur' | 'onKeyUp' | 'onFocus'
  >;

type InnerInputProps = {
  /** Type of the input value */
  type?: SupportedInputType;
  /** The value that is set to the input */
  value?: string;
  confirmed?: BaseInputCommonProps['confirmed'];
  errorText?: BaseInputCommonProps['errorText'];
  /** OnChange handler for the input */
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    value: string
  ) => void;
  /** Placeholder is visible when the input is empty */
  placeholder?: string;
  /** Custom id */
  id?: string;
  /** Name attribute passed to the input element */
  name?: string;
  /** Autocomplete attribute passed to the input element */
  autocomplete?: string;
  /** Icon to be displayed. Is not visible if input is in the errornous or confirmed state  */
  iconName?: SupportedIconName;
  /** Customizable color for the icon */
  iconColor?: SupportedColor;
  /** OnClick handler for the icon */
  onIconClick?: () => void;
  /** Readonly input */
  readonly?: boolean;
  /** Border radius for the inner input for the autofill border hack */
  borderRadius?: BorderRadiusProps;
  /** Overridable tabIndex for the internal input element. By default input is focusable unless the it's defined as readonly or disbaled */
  inputTabIndex?: number;
} & Omit<
  JSX.IntrinsicElements['input'],
  'id' | 'value' | 'name' | 'children' | 'className' | 'onChange' | 'ref'
>;

// When children are used to replace input element.
// For example in DropdownMultiselect AmountChip component is rendered instead of input element.
export type BaseInputPropsWithChildren = BaseInputCommonProps & {
  children: React.ReactElement | React.ReactNodeArray;
};

// When native input element is in use.
export type BaseInputPropsWithoutChildren = BaseInputCommonProps &
  InnerInputProps & {
    children?: undefined;
  };

type BaseInputProps =
  | BaseInputPropsWithChildren
  | BaseInputPropsWithoutChildren;

const BaseInput = forwardRef(
  (
    {
      label,
      required,
      className,
      onClick,
      onBlur,
      onFocus,
      onKeyUp,
      borderRadius,
      wrapperTabIndex,
      showCustomLabel,
      helperText,
      disabled,
      ...props
    }: BaseInputProps,
    ref: React.Ref<HTMLInputElement>
  ) => {
    const inputElement = useRef<HTMLInputElement>(null);

    const handleInputFocus = () => {
      /* istanbul ignore else  */
      if (!ref) {
        inputElement.current?.focus();
      }
    };

    const resolveLabel = label ? (
      required ? (
        <>
          <Span>{label} </Span>
          <Span className={styles.input_required}>*</Span>
        </>
      ) : (
        label
      )
    ) : (
      <Span>&nbsp;</Span>
    );

    return (
      <Div
        className={[styles.input, className]}
        onClick={onClick}
        onBlur={onBlur}
        onKeyUp={onKeyUp}
        onFocus={onFocus}
        tabIndex={disabled ? undefined : wrapperTabIndex}
      >
        {!showCustomLabel && (
          <>
            <BodyText
              size="sm"
              block
              color={disabled ? 'gray400' : 'black'}
              tagName="label"
              htmlFor={!props.children && props.id ? props.id : undefined}
            >
              {resolveLabel}
            </BodyText>
            <Gutter size="xs" />
          </>
        )}
        <Flex
          centered
          className={{
            [styles.input__container]: true,
            [styles.input__container_error]:
              !!props.errorText || props.errorText === ' ',
            [styles.input__container_disabled]: disabled,
          }}
          borderRadius={borderRadius || { size: 'sm' }}
          backgroundColor={!!disabled ? 'gray200' : 'white'}
          onClick={!!disabled ? undefined : handleInputFocus}
        >
          {props.children ? (
            <>{props.children}</>
          ) : (
            renderInput(
              { ...props, borderRadius, disabled },
              ref || inputElement
            )
          )}
        </Flex>

        {!!props.errorText && props.errorText !== ' ' && (
          <InputMessage text={props.errorText} color="red600" />
        )}
        {!!helperText && <InputMessage text={helperText} color="gray500" />}
      </Div>
    );
  }
);

const renderInput = (
  {
    value,
    placeholder,
    type = 'text',
    id,
    name,
    autocomplete,
    disabled,
    readonly,
    onIconClick,
    errorText,
    confirmed,
    iconName,
    iconColor,
    onChange,
    borderRadius,
    inputTabIndex,
    ...props
  }: InnerInputProps,
  ref: React.Ref<HTMLInputElement>
) => {
  const inputNotFocusable = !!disabled || !!readonly;
  const hasIcon: boolean = !!iconName || !!errorText || !!confirmed;

  const handleKeyPress = (event: React.KeyboardEvent) => {
    event.key === 'Enter' && onIconClick?.();
  };

  const classNames = cn('inner-input-test', styles.input__inputField, {
    [styles.input__inputField_withIcon]: hasIcon,
    [styles.input__inputField_disabled]: disabled,
    ...getBorderRadiusClasses(borderRadius || { size: 'sm' }),
  });

  return (
    <>
      <input
        value={value || ''} // The default value is here for keeping the input element controlled. If value is first undefined and then given it will cause an error.
        placeholder={placeholder}
        onChange={
          !!readonly
            ? undefined
            : onChange
            ? (event) => onChange(event, event.target.value)
            : undefined
        }
        disabled={disabled}
        ref={ref}
        type={type}
        id={id}
        name={name}
        className={classNames}
        readOnly={!!readonly}
        autoComplete={autocomplete}
        tabIndex={
          inputNotFocusable ? undefined : inputTabIndex ? inputTabIndex : 0
        }
        {...props}
      />
      {hasIcon && (
        <ResolveIcon
          errorText={errorText}
          confirmed={confirmed}
          iconColor={iconColor}
          iconName={iconName}
          svgFocusable={false}
          handleKeyPress={handleKeyPress}
          disabled={disabled}
          onIconClick={onIconClick}
        />
      )}
    </>
  );
};

export const supportedInputTypes = [
  'email',
  'password',
  'tel',
  'text',
  'url',
  'search',
] as const;

export type SupportedInputType = ArrayElementType<typeof supportedInputTypes>;

export default BaseInput;
